"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.contractFinanceSchema = exports.financeBucketSchema = exports.financePersonSchema = exports.lineItemFinanceSchema = exports.occurrenceFinanceSchema = exports.periodBandSchema = exports.PERIOD_BANDS = exports.MARGIN_MODES = exports.FINANCE_GRANULARITIES = exports.SLIP_UNITS = exports.FORMULA_EACS = exports.earnedWeightsSchema = exports.EARNED_WEIGHT = void 0;
exports.statusWeightsOf = statusWeightsOf;
exports.earnedHoursOf = earnedHoursOf;
exports.defaultGranularity = defaultGranularity;
exports.cpi = cpi;
exports.spi = spi;
exports.eac = eac;
exports.etc = etc;
exports.vac = vac;
exports.tcpi = tcpi;
exports.earnedValueOfPeriods = earnedValueOfPeriods;
exports.plannedCostOrFrame = plannedCostOrFrame;
exports.plannedValueOrFrame = plannedValueOrFrame;
exports.runRate = runRate;
exports.projectedCost = projectedCost;
exports.cumulativeSpend = cumulativeSpend;
exports.forecastLines = forecastLines;
exports.forecastHours = forecastHours;
exports.lineItemForecast = lineItemForecast;
exports.burnRatio = burnRatio;
exports.externalCharges = externalCharges;
exports.chartHourRate = chartHourRate;
exports.workdaysBetween = workdaysBetween;
const eac_1 = require("./eac");
const contract_1 = require("./contract");
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ EVM — C58, Phase 4. Earned value over a contract.                         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Six letters carry this whole screen and each one is a ratio somebody will misread:
 *
 * | | | |
 * |---|---|---|
 * | **BAC** | budget at completion | the cost frame, not the revenue |
 * | **AC**  | actual cost          | hours × the person's cost rate, plus external costs |
 * | **EV**  | earned value         | how much of the frame the work done is *worth* |
 * | **PV**  | planned value        | how much of the frame the plan said we would have spent by now |
 * | **EAC** | estimate at completion | what the whole thing will cost |
 * | **ETC** | estimate to complete | what is left to spend |
 *
 * ⚠️⚠️ **EV IS NOT AC AND THE WHOLE METHOD IS THAT DISTINCTION.** *Actual cost* is what we spent;
 * *earned value* is what we got for it, priced in the same frame. A project that has spent half its
 * budget has AC = BAC/2 and tells you nothing; one that has spent half its budget and earned a third
 * of it is 33% done at 50% of the money, which is the sentence CPI states as 0.67.
 *
 * ⚠️ **Every function here returns `null` rather than a number it cannot honestly produce.** A CPI
 * of `Infinity` because nothing has been spent, or an SPI of 12 because the plan for today was
 * eleven forints, are worse than a dash: they are plausible-looking figures that a reader acts on.
 */
/** Below this, a money figure is treated as zero — `numeric(14,2)` rounds to two places. */
const EPSILON = 0.004;
/**
 * ⚠️ 30 Oct 2026 · **THE DEFAULTS, NOT THE RULE.** Since EV-2 the two middle weights are the
 * workspace's (`0248`); this is what a workspace starts with, and what a caller with no workspace
 * at hand — a test, a fixture — earns by. Open and closed never change.
 */
exports.EARNED_WEIGHT = {
    open: 0,
    in_progress: 0.5,
    in_review: 0.8,
    closed: 1,
};
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 30 Oct 2026 · EV-2 · THE WORKSPACE'S WEIGHTS, AND THE TWO THAT ARE FIXED.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ Added under rule 8. Settings → Tasks → Weights edits the two middle categories; open stays 0
 * and closed stays 1, so the wire carries only what can change.
 */
exports.earnedWeightsSchema = zod_1.z.object({
    inProgress: zod_1.z.number().min(0).max(1),
    inReview: zod_1.z.number().min(0).max(1),
});
function statusWeightsOf(weights) {
    return { open: 0, in_progress: weights.inProgress, in_review: weights.inReview, closed: 1 };
}
/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ 30 Oct 2026 · A FINISHED TASK WITH NO ESTIMATE EARNS WHAT IT TOOK.    │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * `estimate × EARNED_WEIGHT` made a task with no estimate worth nothing however it ended, while its
 * logged hours still counted as cost — so every unestimated ticket a team closed pulled CPI down
 * (UAT, the product owner). Closed, it now counts as if it had been estimated at exactly the hours
 * it took: it earns them, and they join the scope earned is measured against.
 *
 * ⚠️ **Closed only.** An unestimated task still in progress has no scope to take a share of, and
 * crediting its hours as they are logged would make every hour spent on one look fully earned.
 *
 * - `earned` — what it has earned, in hours.
 * - `scope` — what it is worth when done: its estimate, or for that task its logged hours. A
 *   percent-complete is `Σ earned ÷ Σ scope`; dividing by the bare estimate instead would let a
 *   finished deliverable earn more than its budget.
 */
function earnedHoursOf(task, weights = exports.EARNED_WEIGHT) {
    if (task.estimate > 0) {
        return { earned: task.estimate * weights[task.category], scope: task.estimate };
    }
    if (task.category === 'closed')
        return { earned: task.logged, scope: task.logged };
    return { earned: 0, scope: 0 };
}
/**
 * ⚠️⚠️ **THE TWO EAC FORMULAS ANSWER DIFFERENT QUESTIONS AND THE SCREEN LETS SOMEBODY PICK.**
 *
 * - `cpi` — *trendtartó*: `BAC / CPI`. Assumes today's efficiency holds to the end. Right when the
 *   overspend is systemic — a team that is slower than estimated stays slower.
 * - `etc` — *tervtartó*: `AC + (BAC − EV)`. Assumes the remainder goes exactly to plan. Right when
 *   the overspend was a one-off — a bad month that has been dealt with.
 *
 * Defaulting to one and hiding the other would make the dashboard state an opinion as a fact.
 */
exports.FORMULA_EACS = ['cpi', 'etc'];
/**
 * ⚠️⚠️ **A SLIP IS COUNTED IN ONE OF TWO UNITS AND THEY DISAGREE BY A FACTOR APPROACHING TWO.**
 * Five working days late and seven calendar days late are the same slip; a delivery lead plans in
 * the first and a client hears the second. The mockup puts the choice on the unit itself, and
 * picking one silently would make the same delay read as two different sizes of problem.
 */
exports.SLIP_UNITS = ['workday', 'calendar'];
/**
 * 16 Oct 2026 · the chart's columns: a day, a week or a month each — the mockup's *Nap / Hét /
 * Hónap*. A recurring contract's columns stay its periods, as the mockup draws them.
 */
exports.FINANCE_GRANULARITIES = ['day', 'week', 'month'];
/**
 * The mockup's default: a day each up to 40 working days, a week each up to 200, a month beyond —
 * `wd(d0, d1) <= 40 ? 'day' : wd(d0, d1) <= 200 ? 'week' : 'month'`.
 */
function defaultGranularity(from, to) {
    const days = workdaysBetween(from, to);
    return days <= 40 ? 'day' : days <= 200 ? 'week' : 'month';
}
/** Which margin the tiles show — the plan's, or the forecast's. The mockup's own `fdMar`. */
exports.MARGIN_MODES = ['plan', 'forecast'];
/**
 * CPI — cost performance index. How much frame-value a spent forint bought.
 *
 * ⚠️ `null` when nothing has been spent: `EV / 0` is not "infinitely efficient".
 */
function cpi(earnedValue, actualCost) {
    if (actualCost <= EPSILON)
        return null;
    return earnedValue / actualCost;
}
/**
 * SPI — schedule performance index. Earned value against what the plan said we would have spent.
 *
 * ⚠️⚠️ **GUARDED AGAINST A TINY PV, NOT JUST A ZERO ONE.** A plan that had 3 000 Ft of work booked
 * by today against a 6 M budget divides into a number like 400, and *"SPI 400"* reads as a project
 * four hundred times ahead of schedule rather than as a plan nobody filled in. The mockup's own
 * guard is the same: PV must be at least 1% of the budget.
 */
function spi(earnedValue, plannedValue, budget) {
    if (plannedValue <= EPSILON || plannedValue <= budget * 0.01)
        return null;
    return earnedValue / plannedValue;
}
/**
 * EAC — what the whole thing will cost. See `EAC_MODES` for why there are two.
 *
 * ⚠️⚠️ **`index` IS THE CPI THE SCREEN SHOWS, WHEN THE CALLER HAS ONE.** The finance tab's CPI
 * tile is measured in HOURS (earned hours ÷ logged hours — the mockup's definition, and the one the
 * Dashboard tab divides too), while this function used to recompute CPI from MONEY. So the
 * *Trendtartó EAC* printed beside the tile was not `BAC ÷` the CPI printed in the tile, and a
 * reader checking it by hand got a different number. Passing the tile's own index makes the pair
 * agree by construction. Omitted, it is `cpi(earnedValue, actualCost)` — what the hours dashboard
 * wants, since its earned value and actual are already hours.
 */
function eac(mode, budget, actualCost, earnedValue, index) {
    if (mode === 'etc')
        return actualCost + Math.max(0, budget - earnedValue);
    const performance = index === undefined ? cpi(earnedValue, actualCost) : index;
    return performance === null || performance <= EPSILON ? null : budget / performance;
}
/** ETC — what is left to spend. ⚠️ Floored: a finished job does not have negative work left. */
function etc(estimateAtCompletion, actualCost) {
    if (estimateAtCompletion === null)
        return null;
    return Math.max(0, estimateAtCompletion - actualCost);
}
/** VAC — how far inside or outside the frame we will land. ⚠️ Negative is an overrun. */
function vac(budget, estimateAtCompletion) {
    if (estimateAtCompletion === null)
        return null;
    return budget - estimateAtCompletion;
}
/**
 * TCPI — the efficiency the remaining work needs in order to land inside the frame.
 *
 * ⚠️ `null` when the frame is already exactly spent: the denominator is zero, and the honest answer
 * to *"how efficient must we be with no money left"* is not a number.
 */
function tcpi(budget, earnedValue, actualCost) {
    /* 30 Oct 2026 · EV · past the budget its sign flips and it means nothing — not shown. */
    if (actualCost > budget || Math.abs(budget - actualCost) <= EPSILON)
        return null;
    return (budget - earnedValue) / (budget - actualCost);
}
/**
 * ⚠️⚠️ **EARNED VALUE FOR A RECURRING CONTRACT IS TIME, NOT PROGRESS, AND THAT IS NOT LAZINESS.**
 * A retainer buys availability for a month; when the month is over the client has had what they
 * paid for whether or not a particular ticket closed. So a closed period has earned its whole
 * budget and the current one has earned the share of it that has elapsed. Applying the task-status
 * weighting used for a fixed deliverable would make a quiet support month look like a failure.
 */
function earnedValueOfPeriods(periods) {
    return periods.reduce((total, period) => {
        if (period.state === 'past')
            return total + period.budget;
        if (period.state !== 'current' || period.workdays <= 0)
            return total;
        return total + period.budget * (period.workdaysDone / period.workdays);
    }, 0);
}
/**
 * ⚠️⚠️ **THE TWO "THIN" FALLBACKS, AND THEY EXIST BECAUSE OF WHAT A REAL WORKSPACE LOOKS LIKE.**
 *
 * A two-year retainer is planned one quarter ahead — nobody allocates people to next October. So
 * the allocation covers a fraction of the contract, and *tervezett költés* computed from it reads
 * as a project that has planned 8% of its budget. Which is true about the allocation and false
 * about the plan.
 *
 * When the allocation covers less than half the frame, the frame ITSELF is the better statement of
 * what we intend to spend, and the screen says so in the formula line rather than quietly swapping
 * the number. The same argument applies to PV, with a tighter trigger: 5%.
 */
function plannedCostOrFrame(plannedFromAllocation, budget) {
    const thin = budget > EPSILON && plannedFromAllocation < budget * 0.5;
    return { value: thin ? budget : plannedFromAllocation, fromFrame: thin };
}
/** The same fallback for PV, prorated by elapsed time. ⚠️ 5%, not 50% — see above. */
function plannedValueOrFrame(plannedToDate, budget, elapsedShare) {
    const thin = budget > EPSILON && plannedToDate < budget * 0.05;
    if (!thin)
        return { value: plannedToDate, fromFrame: false };
    return { value: budget * Math.max(0, Math.min(1, elapsedShare)), fromFrame: true };
}
/**
 * The run rate and what it projects.
 *
 * ⚠️ **Per WORKING day, not per calendar day.** A period's spend happens on the days people work;
 * dividing by calendar days understates the rate by two sevenths and makes every period look like
 * it will land comfortably inside its frame.
 */
function runRate(cost, workdaysDone) {
    if (workdaysDone <= 0)
        return 0;
    return cost / workdaysDone;
}
/**
 * ⚠️ **Before any day has elapsed the projection is the BUDGET, not zero.** A period that has not
 * started has no evidence either way, and projecting nothing would print a confident *"−504 000 Ft
 * overrun"* — a saving nobody has made.
 */
function projectedCost(cost, workdays, workdaysDone, budget) {
    if (workdaysDone <= 0)
        return budget;
    return runRate(cost, workdaysDone) * workdays;
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ U-2 · the chart's spend line, measured then planned.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE FUTURE SPENDS ITS PLANNED COST, NEVER A SLICE OF THE BUDGET.** The line used to add
 * `bucket.budget` for every bucket after today — and for a contract with no periods that is the
 * budget divided evenly by the bucket count, so the line ended EXACTLY on the budget whatever the
 * plan said. A project planned at 88.1 M against an 84 M frame drew a forecast that landed on 84 M:
 * the one picture the chart exists to draw, made impossible by arithmetic.
 *
 * ⚠️ **Every bucket adds what it spent plus what is still planned in it after today.** Past:
 * the measurement alone (nothing is planned after today in it). Current: actual to date plus the
 * plan for the rest of the bucket. Future: the plan. One sum, no branch on the bucket's tense.
 *
 * ⚠️ **`fromFrame` is the TERVEZETT KÖLTÉS tile's own fallback** (`plannedCostOrFrame`). When the
 * allocation covers less than half the frame, the tile shows the frame — and so does the line: a
 * bucket in the future adds its budget. Drawing a flat line there would say *we will spend nothing
 * more*, which is what a thin plan looks like and not what anybody intends.
 */
function cumulativeSpend(buckets, fromFrame) {
    let running = 0;
    return buckets.map((bucket) => {
        const ahead = fromFrame && bucket.future ? Number(bucket.budget) : Number(bucket.plannedCostAhead);
        running += Number(bucket.cost) + ahead;
        return running;
    });
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ One mode's spend and income on the chart: measured, then its own walk.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE LINES HAVE TO END ON THE TILES** (15 Oct 2026, *always the forecast*). They do by
 * construction: the server walks each mode's ETC — its EAC less everything the buckets measured
 * and every external cost dated ahead — into the buckets (`ahead`), so the running totals reach
 * that mode's ELŐREJELZETT KÖLTSÉG and BEVÉTEL. EV-10 replaced spreading the EAC over the plan
 * in proportion: the shape between today and the end now follows the mode too, and stops rising
 * on the mode's own finish date.
 *
 * External costs dated after today land in their own bucket (decision e), on every mode alike.
 */
function forecastLines(buckets, mode) {
    let spent = 0;
    let earned = 0;
    const spend = [];
    const income = [];
    for (const bucket of buckets) {
        const ahead = bucket.ahead[mode];
        spent += Number(bucket.cost) + Number(bucket.externalAhead) + Number(ahead?.cost ?? 0);
        earned += Number(bucket.revenue) + Number(ahead?.revenue ?? 0);
        spend.push(spent);
        income.push(earned);
    }
    return { spend, income };
}
/**
 * 2 Nov 2026 · **the hours column beside the line, in one mode.** Each bucket's logged hours plus
 * the hours the mode's walk spends there (`ahead[mode].hours`), so a future column rises with the
 * dashed spend line and flattens where the mode finishes. Per bucket and running, like the
 * columns' own *Periódus / Kumulált* switch. A past bucket's walk is empty: its figure is logged.
 */
function forecastHours(buckets, mode) {
    let running = 0;
    const perPeriod = [];
    const cumulative = [];
    for (const bucket of buckets) {
        const one = Number(bucket.loggedHours) + Number(bucket.ahead[mode]?.hours ?? 0);
        running += one;
        perPeriod.push(one);
        cumulative.push(running);
    }
    return { perPeriod, cumulative };
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 16 Oct 2026 · ONE LINE ITEM'S FORECAST, IN BOTH EAC MODES — decisions a–d. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **PER LINE ITEM, AND THE CONTRACT ADDS THEM UP (d).** One CPI over a contract mixing a fixed
 * build with T&M support would let the support hours distort the build's efficiency. So each line
 * item forecasts by its own kind, and the contract's EAC and forecast revenue are the sums.
 *
 * **A fixed, licence or non-billable item** — the EVM formulas over its own figures:
 *  - *trendtartó* `BAC ÷ CPI`, CPI its own earned ÷ logged HOURS. Nothing logged yet: its budget.
 *  - *tervtartó* `AC + (BAC − EV)`, which does not change.
 *  - No budget at all: what it has spent.
 *  - Revenue: its agreed sum (U-1) whichever mode.
 *
 * **A time-and-material item** — the allocation is its plan:
 *  - *tervtartó* (a): the remaining allocation is used in full — `AC + allocation cost ahead`.
 *  - *trendtartó* (b): the remaining allocation × its BURN RATIO, logged ÷ allocated so far on this
 *    line item. Nothing allocated yet: a ratio of one.
 *  - Revenue (c): what its logged hours earned plus the forecast hours ahead × its billing rate.
 *
 * ⚠️ `actualCost` is LABOUR — external costs are added once, at the contract (decision e).
 */
function lineItemForecast(input) {
    if (input.type === 'time_and_material') {
        const ratio = burnRatio(input.loggedHours, input.plannedHoursToDate);
        const rate = input.billingRatePerHour ?? 0;
        return {
            etc: {
                cost: input.actualCost + input.plannedCostAhead,
                revenue: input.earnedRevenue + input.plannedHoursAhead * rate,
            },
            cpi: {
                cost: input.actualCost + input.plannedCostAhead * ratio,
                revenue: input.earnedRevenue + input.plannedHoursAhead * ratio * rate,
            },
        };
    }
    if (input.budget <= EPSILON) {
        return {
            etc: { cost: input.actualCost, revenue: input.agreedRevenue },
            cpi: { cost: input.actualCost, revenue: input.agreedRevenue },
        };
    }
    const index = cpi(input.earnedHours, input.loggedHours);
    return {
        etc: {
            cost: input.actualCost + Math.max(0, input.budget - input.earnedValue),
            revenue: input.agreedRevenue,
        },
        cpi: {
            cost: index === null || index <= EPSILON ? input.budget : input.budget / index,
            revenue: input.agreedRevenue,
        },
    };
}
/**
 * (b) · how much of its allocation a T&M line item actually logs: logged ÷ allocated so far. `1`
 * when nothing was allocated yet — there is no trend to read, so the plan stands.
 */
function burnRatio(loggedHours, plannedHoursToDate) {
    return plannedHoursToDate > EPSILON ? loggedHours / plannedHoursToDate : 1;
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 16 Oct 2026 · WHEN AN EXTERNAL COST IS CHARGED — decision e.              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A one-off on its own day. A recurring cost on EVERY recurrence, by its own unit and step, from
 * the day it was incurred to its `recurrence_until` — or to `horizon` when it has none.
 *
 * ⚠️ **Only where no recurring contract carries it.** A recurring cost on a recurring contract is
 * charged on each of the contract's periods it touches (`0132`); the caller keeps that rule, which
 * the external costs list counts the same way.
 */
function externalCharges(cost, horizon) {
    if (cost.recurrenceUnit === null)
        return [cost.on];
    const until = cost.recurrenceUntil ?? horizon;
    const step = Math.max(1, cost.recurrenceStep ?? 1);
    const out = [];
    const [year, month, day] = cost.on.split('-').map(Number);
    for (let index = 0; index < 1000; index += 1) {
        const at = cost.recurrenceUnit === 'week'
            ? new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + index * step * 7))
            : /* ⚠️ The 31st of a short month is that month's last day, not the next month's first. */
                new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1 + index * step, Math.min(day ?? 1, new Date(Date.UTC(year ?? 1970, (month ?? 1) + index * step, 0)).getUTCDate())));
        const on = at.toISOString().slice(0, 10);
        if (on > until)
            break;
        out.push(on);
    }
    return out.length === 0 ? [cost.on] : out;
}
/**
 * U-2 · ⚠️ **What one hour is drawn as on the finance chart's single axis.** Planned cost ÷ planned
 * hours, so a planned column's top meets the planned-cost line; failing a plan, actual labour cost ÷
 * logged hours; failing both, `null` — and the chart falls back to two axes rather than invent a
 * rate.
 */
function chartHourRate(planned, logged) {
    if (planned.hours > EPSILON && planned.money > EPSILON)
        return planned.money / planned.hours;
    if (logged.hours > EPSILON && logged.money > EPSILON)
        return logged.money / logged.hours;
    return null;
}
/** Mon–Fri between two wire dates, inclusive, minus the days the workspace has closed. */
function workdaysBetween(from, to, 
/** ⚠️ Every holiday in the window, INCLUDING `workday` ones — a relocated working Saturday. */
holidays = new Map()) {
    if (from === '' || to === '' || to < from)
        return 0;
    let count = 0;
    const cursor = new Date(`${from}T00:00:00Z`);
    const end = new Date(`${to}T00:00:00Z`);
    while (cursor <= end) {
        const day = cursor.toISOString().slice(0, 10);
        const marked = holidays.get(day);
        if (marked === 'workday')
            count += 1;
        else if (marked === undefined && cursor.getUTCDay() !== 0 && cursor.getUTCDay() !== 6)
            count += 1;
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return count;
}
/* ── the wire ──────────────────────────────────────────────────────────────── */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** ⚠️ Every money figure is a string, like every other `numeric` on the wire. */
const money = zod_1.z.string();
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 30 Oct 2026 · ONE PERIOD, SPLIT BY WHAT KIND OF WORK THE MONEY IS.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **ADDED UNDER RULE 8.** The recurring contract's *budget difference* and *profit* charts
 * (the legacy Forecastify's two stacked charts) stack one band per line-item type plus one for
 * external costs. Raw figures only — the chart derives `budget − cost` and `revenue − cost`.
 *
 * ⚠️ **A current or future period is a FORECAST**, and its figures say so (`forecast` on the
 * period): its budget and fixed income are known (they repeat every period), its spend is the
 * allocation still ahead priced at each person's cost rate, and a T&M item's income is those
 * allocated hours at its billing rate. A past period is measured only.
 */
exports.PERIOD_BANDS = [...contract_1.LINE_ITEM_TYPES, 'external'];
exports.periodBandSchema = zod_1.z.object({
    kind: zod_1.z.enum(exports.PERIOD_BANDS),
    /** The per-period budget of this kind's retainer line items; `0` for external costs. */
    budget: money,
    /** Logged cost, plus the allocation still ahead in a current or future period. */
    cost: money,
    /** What this kind earns in the period — known for a fixed item, hours × rate for T&M. */
    revenue: money,
});
exports.occurrenceFinanceSchema = zod_1.z.object({
    index: zod_1.z.number().int(),
    startsOn: wireDateSchema,
    endsOn: wireDateSchema,
    state: zod_1.z.enum(['past', 'current', 'future']),
    reopened: zod_1.z.boolean(),
    budget: money,
    /** The retainer revenue — what the frame itself earns. */
    revenue: money,
    /**
     * ⚠️ **Over-frame revenue, kept SEPARATE.** A `billable` line item neither carries nor consumes
     * the frame, so folding it into `revenue` would make a period look like it had earned its
     * retainer when it had not.
     */
    billableRevenue: money,
    /** Hours × each person's cost rate on the day, plus external costs when they are counted in. */
    cost: money,
    costRetainer: money,
    costBillable: money,
    externalCost: money,
    invoiced: money,
    /** The most recent non-void invoice's external number, for the table's SZÁMLA column. */
    invoiceExternalId: zod_1.z.string().nullable(),
    loggedHours: money,
    plannedHours: money,
    plannedCost: money,
    budgetOverridden: zod_1.z.boolean(),
    revenueOverridden: zod_1.z.boolean(),
    /** 30 Oct 2026 · current or future: the period's figures include the allocation ahead. */
    forecast: zod_1.z.boolean(),
    /** 30 Oct 2026 · one entry per `PERIOD_BANDS`, in that order — `periodBandSchema`. */
    byType: zod_1.z.array(exports.periodBandSchema),
});
exports.lineItemFinanceSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** R-57 · the address the screen links to. */
    identifier: zod_1.z.string(),
    /**
     * U-4 · ⚠️ **The CONTRACT's public identifier, on every row.** The line item's own address is
     * `/contracts/<contract>/<item>`, and on the whole-project view the rows come from several
     * contracts — the view's own identity no longer says which.
     */
    contractIdentifier: zod_1.z.string(),
    number: zod_1.z.number().int(),
    name: zod_1.z.string(),
    /* ⚠️⚠️ **`LINE_ITEM_TYPES`, NOT A COPY OF ITS MEMBERS.** Three schemas each inlined the
       same three strings, so C55's `license` typechecked in `contract.ts` and silently
       disagreed here — caught by the compiler only because the API assigns between them. */
    type: zod_1.z.enum(contract_1.LINE_ITEM_TYPES),
    scope: zod_1.z.enum(['retainer', 'billable']),
    startsOn: wireDateSchema,
    endsOn: wireDateSchema,
    budget: money,
    /**
     * U-1 · ⚠️⚠️ **THE AGREED INCOME, NOT WHAT HAS BEEN INVOICED.** A fixed or licence item at its
     * price, a T&M one at what it has earned plus its remaining estimate × rate, a non-billable one
     * at nothing. `invoiced` beside it is the other figure; they are equal only by coincidence.
     */
    revenue: money,
    cost: money,
    externalCost: money,
    invoiced: money,
    invoiceExternalId: zod_1.z.string().nullable(),
    loggedHours: money,
    plannedHours: money,
    plannedCost: money,
    estimateHours: money,
    /**
     * ⚠️ **Earned HOURS, weighted by task status** — the C58 half of earned value for a fixed
     * deliverable. A task in progress is not worth nothing and is not worth its whole estimate.
     */
    earnedHours: money,
    earnedValue: money,
    /* ── 16 Oct 2026 · the forecast's inputs and its two answers (decisions a–d) ── */
    /** The allocation up to today, and after it. */
    plannedHoursToDate: money,
    plannedHoursAhead: money,
    plannedCostAhead: money,
    /** T&M: what its logged hours earned. Zero for every other type. */
    earnedRevenue: money,
    /** Its EAC and forecast revenue in each mode — `lineItemForecast`. ⚠️ Labour only. */
    forecast: zod_1.z.object({
        cpi: zod_1.z.object({ cost: money, revenue: money }),
        etc: zod_1.z.object({ cost: money, revenue: money }),
    }),
});
/**
 * One person's share of the spend, planned and actual.
 *
 * ⚠️ **Per PERSON, and the screen regroups.** The mockup offers the breakdown by seniority, by role
 * and by person; sending three aggregations would be three chances for them to disagree about a
 * total, and the client can add up rows.
 */
exports.financePersonSchema = zod_1.z.object({
    memberId: zod_1.z.string(),
    name: zod_1.z.string(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from.** The normalised email,
     * produced by `wireAvatarSeed`. This shape carries no avatar URL, which is why the first
     * pass missed it — it went looking for pictures and this row only ever had initials.
     */
    avatarSeed: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **The person's PROJECT role (`0058`), not their permission role.** The breakdown asks
     * *who did what on this project* — a developer and a tech lead who are both plain MEMBERs of the
     * workspace are two rows, not one. The membership overlapping the scope's window decides, the
     * latest when there are several. `null`: not a member of the project in that window, or a member
     * with no role — the screen's *Nincs projektszerepkör* row.
     */
    roleName: zod_1.z.string().nullable(),
    /** The project role's own colour, `#RRGGBB`, for the breakdown's dot. `null` with no role. */
    roleColor: zod_1.z.string().nullable(),
    /** C58 · ⚠️ `null` means nobody has said — `0135`, and NOT a default of `medior`. */
    seniority: zod_1.z.enum(['junior', 'medior', 'senior', 'lead', 'principal']).nullable(),
    /** R-60 · every team the person is in, by name — today's, not the window's. */
    teams: zod_1.z.array(zod_1.z.string()),
    plannedHours: money,
    plannedCost: money,
    loggedHours: money,
    actualCost: money,
    /** ⚠️ True when this person has no rate card in force — their hours are priced at nothing. */
    unrated: zod_1.z.boolean(),
});
/** One column of the chart. ⚠️ Already bucketed server-side — the client draws, it does not fold. */
/** EV-10 · one mode's walk in one bucket: what it still spends, earns, and the hours behind it. */
const aheadFlowSchema = zod_1.z.object({ cost: money, revenue: money, hours: money });
exports.financeBucketSchema = zod_1.z.object({
    key: zod_1.z.string(),
    label: zod_1.z.string(),
    from: wireDateSchema,
    to: wireDateSchema,
    future: zod_1.z.boolean(),
    loggedHours: money,
    plannedHours: money,
    /**
     * U-2 · ⚠️ **The bucket's planned COST** — its allocation days priced at each person's cost rate
     * on the day, the source the TERVEZETT KÖLTÉS tile sums. The chart used to have only planned
     * hours and drew the future at a slice of the budget instead.
     */
    plannedCost: money,
    /**
     * U-2 · ⚠️ **The part of `plannedCost` dated after today.** All of it in a future bucket, none in
     * a past one, the rest of the bucket in the current one — so `cost + plannedCostAhead` is the
     * bucket's spend, measured where it can be and planned where it cannot. `cumulativeSpend` sums it.
     */
    plannedCostAhead: money,
    budget: money,
    /**
     * Labour at the day's cost rate, plus external costs when the switch counts them in — on every
     * contract type, so the line's last measured point is the TÉNY KÖLTÉS tile.
     */
    cost: money,
    revenue: money,
    margin: money,
    /**
     * 16 Oct 2026 · ⚠️ **External costs dated after today** (decision e), apart from `cost`, which is
     * what has been spent. They land in their own bucket on the forecast line.
     */
    externalAhead: money,
    /**
     * ┌───────────────────────────────────────────────────────────────────────┐
     * │ 31 Oct 2026 · EV-10 · WHAT EACH OF THE FIVE MODES STILL SPENDS HERE.  │
     * └───────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ ADDED UNDER RULE 8, replacing the two-reading `revenueAhead`. The mode's ETC walked day
     * by day (`walkEtc`) and summed into the bucket: `cost` is labour still to be spent here, and
     * `revenue` what those hours bill. So `cost + externalAhead + ahead[mode].cost` is the bucket's
     * forecast spend and `revenue + ahead[mode].revenue` its forecast income — and their running
     * totals end on the mode's tiles. ⚠️ A bucket after the scope's end exists only to hold these:
     * a forecast that overruns the deadline is drawn past it.
     *
     * ⚠️ 2 Nov 2026 · ADDED UNDER RULE 8: `hours`, the hours behind `cost` — so `loggedHours +
     * ahead[mode].hours` is the bucket's forecast hours, and the hours column rises with the line.
     */
    ahead: zod_1.z.object({
        budget: aheadFlowSchema,
        trend: aheadFlowSchema,
        schedule: aheadFlowSchema,
        resource: aheadFlowSchema,
        adjusted: aheadFlowSchema,
    }),
});
exports.contractFinanceSchema = zod_1.z.object({
    /**
     * U-4 · ⚠️⚠️ **`project` IS THE WHOLE PROJECT, AND THEN THE FOUR `contract*` FIELDS NAME THE
     * PROJECT.** Every contract summed in the workspace's default currency, plus the hours logged on
     * tasks with no line item at their cost rate (no revenue) and the project's own external costs.
     * It has no periods — `recurring` is false and its buckets are calendar months.
     */
    level: zod_1.z.enum(['contract', 'project']),
    contractId: zod_1.z.string(),
    contractCode: zod_1.z.string(),
    /** R-57 · the contract's public identifier, its address. */
    contractIdentifier: zod_1.z.string(),
    contractName: zod_1.z.string(),
    recurring: zod_1.z.boolean(),
    currencyCode: zod_1.z.string(),
    /** The scope's own window — the whole contract, one line item, or one period. */
    from: wireDateSchema,
    to: wireDateSchema,
    today: wireDateSchema,
    budget: money,
    revenue: money,
    revenueRetainer: money,
    revenueBillable: money,
    actualCost: money,
    actualCostRetainer: money,
    actualCostBillable: money,
    externalCost: money,
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ WHAT THE PROJECT SPENT THAT BELONGS TO NO CONTRACT.                  │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * A cost can be linked to a line item, to a period, or to the PROJECT and nothing finer — and the
     * third kind is real money that this contract's figures deliberately exclude, because attributing
     * it to one of a project's four contracts would be an invention and adding it to each would count
     * it four times. The mockup's own `ecC` filter drops it for the same reason.
     *
     * ⚠️ **Which made it invisible, and that was the defect.** Somebody added a 15 M licence to the
     * project and the project's money tab did not move — correct arithmetic, and no way to tell it
     * from a screen that had failed to load. It was reported beside the contract's own external cost
     * rather than inside it: visible, attributable, and never double-counted.
     *
     * ⚠️⚠️ **4 NOV 2026, THE PRODUCT OWNER: IT IS INSIDE `externalCost` NOW, ON EVERY CONTRACT** — each
     * carries the project's own costs in full, and the project read still counts them once. This
     * field is therefore `0.00` on both reads; it is kept rather than removed so the wire shape and
     * the Agent's rendering of it do not change under a client already deployed.
     */
    projectExternalCost: money,
    loggedHours: money,
    plannedCost: money,
    plannedHours: money,
    /** ⚠️ True when the figure above came from the FRAME rather than the allocation — see the module. */
    plannedFromFrame: zod_1.z.boolean(),
    plannedValue: money,
    plannedValueFromFrame: zod_1.z.boolean(),
    earnedValue: money,
    /**
     * ┌───────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ EARNED **HOURS**, AND CPI IS COMPUTED FROM THESE RATHER THAN FROM MONEY.  │
     * └───────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **THE FINANCE TAB USED `earnedValue / actualCost` AND THAT IS NOT A CPI.**
     * `earnedValue` is budget × completion — a share of the REVENUE; `actualCost` is hours × the
     * cost rate. One over the other is a margin ratio wearing a CPI label, and it read 2.28 on
     * NEX-C02 while the Dashboard tab read 0.18 for the same contract on the same day.
     *
     * ⚠️ **The mockup defines CPI exactly once, and it is hours**: *"EV / AC (teljesített érték /
     * nyóra)"*. Both tabs now divide the same two numbers, so they cannot disagree.
     */
    earnedHours: money,
    invoiced: money,
    /**
     * ⚠️ **Σ (logged hour × that person's TARGET rate)**, so the client can divide by hours for the
     * *cél óradíj* row. Sent as a total rather than an average because an average of averages is not
     * the average — the weighting has to happen where the per-day rates are.
     */
    targetRateTotal: money,
    /**
     * U-3 · ⚠️ **Σ (planned hour × that person's TARGET rate on the day)**, for *tervezett óradíj* —
     * the client divides by `plannedHours`. A total for the reason `targetRateTotal` is one.
     */
    plannedTargetTotal: money,
    /**
     * U-2 · ⚠️ **What one hour is drawn as on the chart's single axis** — `chartHourRate`: planned
     * cost ÷ planned hours, else actual labour cost ÷ logged hours. `null` when neither exists, and
     * the chart keeps a second axis rather than invent one.
     */
    hourRate: money.nullable(),
    /** What the scope will earn by the end — a fixed item's whole price, a T&M item's projection. */
    forecastRevenue: money,
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ 16 Oct 2026 · THE EAC AND THE FORECAST REVENUE, IN BOTH MODES.            │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️⚠️ **COMPUTED HERE, PER LINE ITEM, AND SUMMED (d)** — `lineItemForecast` over each scoped
     * item, plus every external cost of the scope when the switch counts them in (e). The screen
     * picks the mode; it no longer derives an EAC of its own from contract-wide totals.
     */
    /* 30 Oct 2026 · EV · five modes where there were two (`eac.ts`); a line item keeps cpi / etc. */
    forecast: zod_1.z.object({
        budget: zod_1.z.object({ cost: money, revenue: money }),
        trend: zod_1.z.object({ cost: money, revenue: money }),
        schedule: zod_1.z.object({ cost: money, revenue: money }),
        resource: zod_1.z.object({ cost: money, revenue: money }),
        adjusted: zod_1.z.object({ cost: money, revenue: money }),
    }),
    /** 30 Oct 2026 · EV · the five EACs in money, each with its finish (rule 8). */
    eac: eac_1.eacEvaluationSchema.nullable(),
    /**
     * 16 Oct 2026 · ⚠️ **Every external cost of the scope, dated or not yet**, when the switch counts
     * them in — what the EAC adds (e). `externalCost` is the part already charged.
     */
    externalCostTotal: money,
    /** ⚠️ How many people's hours are priced at NOTHING because they have no rate card in force. */
    unratedMembers: zod_1.z.number().int(),
    /**
     * ⚠️⚠️ **CURRENCIES THAT COULD NOT BE PRICED IN THE DISPLAY CURRENCY, AND WERE ADDED AT 1:1
     * ANYWAY.** The alternative — dropping the amount — understates cost and flatters margin with
     * nothing on the screen to say so, which is why the service converts at a factor of one instead.
     * That makes the total approximate, and an approximate total that does not admit it is the thing
     * this array exists to prevent. Empty is the normal case.
     */
    unconvertedCurrencies: zod_1.z.array(zod_1.z.string()),
    periods: zod_1.z.array(exports.occurrenceFinanceSchema),
    lineItems: zod_1.z.array(exports.lineItemFinanceSchema),
    people: zod_1.z.array(exports.financePersonSchema),
    buckets: zod_1.z.array(exports.financeBucketSchema),
});
//# sourceMappingURL=evm.js.map